/**
 * Phase 3.6 — LimitingVariablesPanel
 *
 * Top design constraints blocking the target. Renders only on
 * ``study.status === "partial"`` AND non-empty ``limiting_variables``.
 *
 * Each row is a horizontal bar:
 *   [variable label]  [bar showing lift_potential normalised]  [pk_gap_closed_pct]
 *      reason tag underneath
 */
import { AlertTriangle } from "lucide-react";

const REASON_LABEL = {
    at_upper_bound: "At upper bound",
    at_lower_bound: "At lower bound",
    saturated: "Saturated by other limits",
    low_sensitivity: "Low local sensitivity",
};

function reasonText(r) {
    if (!r) return null;
    return REASON_LABEL[r] || r.replace(/_/g, " ");
}

function fmtPct(n) {
    if (typeof n !== "number" || Number.isNaN(n)) return "—";
    return `${n.toFixed(1)} %`;
}

export default function LimitingVariablesPanel({
    studyStatus,
    limitingVariables,
}) {
    if (studyStatus !== "partial") return null;
    const list = Array.isArray(limitingVariables) ? limitingVariables : [];
    if (list.length === 0) return null;

    const top5 = list.slice(0, 5);
    const maxLift = top5.reduce(
        (m, v) =>
            typeof v?.lift_potential === "number" && v.lift_potential > m
                ? v.lift_potential
                : m,
        0,
    );
    const safeMax = maxLift > 0 ? maxLift : 1;

    return (
        <div
            className="rounded-md border border-ux-warning/40 bg-ux-warning-soft/20 p-4 space-y-3"
            data-testid="limiting-variables-panel"
        >
            <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-ux-warning" />
                <h3 className="text-[13px] font-medium text-ux-text-1">
                    Top design constraints blocking the target
                </h3>
            </div>

            <ul className="space-y-2.5" data-testid="limiting-variables-list">
                {top5.map((v, idx) => {
                    const lift = typeof v?.lift_potential === "number"
                        ? v.lift_potential
                        : 0;
                    const widthPct = Math.max(2, (lift / safeMax) * 100);
                    const label = v?.label || v?.path || `Variable ${idx + 1}`;
                    const gap = typeof v?.pk_gap_closed_pct === "number"
                        ? v.pk_gap_closed_pct
                        : null;
                    return (
                        <li
                            key={v?.path || idx}
                            className="space-y-1"
                            data-testid={`limiting-variable-row-${idx}`}
                        >
                            <div className="flex items-baseline gap-2 text-[12px]">
                                <span
                                    className="text-ux-text-2 truncate"
                                    style={{ flex: "0 0 200px" }}
                                    title={v?.path}
                                >
                                    {label}
                                </span>
                                <div className="flex-1 h-3 bg-ux-bg-3 rounded overflow-hidden border border-ux-border-1">
                                    <div
                                        className="h-full bg-ux-warning"
                                        style={{ width: `${widthPct}%` }}
                                        data-testid={`limiting-variable-bar-${idx}`}
                                    />
                                </div>
                                <span
                                    className="font-mono tabular-nums text-ux-text-1 w-16 text-right"
                                    data-testid={`limiting-variable-pct-${idx}`}
                                >
                                    {fmtPct(gap)}
                                </span>
                            </div>
                            {reasonText(v?.reason) && (
                                <span
                                    className="inline-block text-[10px] uppercase tracking-wider text-ux-warning bg-ux-warning-soft px-1.5 py-0.5 rounded ml-[208px]"
                                    data-testid={`limiting-variable-reason-${idx}`}
                                >
                                    {reasonText(v.reason)}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ul>

            <p className="text-[11px] text-ux-text-3 italic">
                These variables, if pushed against their upper or lower
                constraint bound, would close the largest portion of the Pk
                gap. Widen their bounds and rerun the study to recover.
            </p>
        </div>
    );
}
